import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Archive,
  CheckCircle2,
  FileText,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  Trash2,
  Undo2
} from 'lucide-react';
import { Page } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/dialog';
import { EmptyState, ErrorState } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, Tabs, TabsList, TabsTrigger } from '@/components/ui/menu';
import { Pagination } from '@/components/ui/pagination';
import { SkeletonRow, TableScroll, TableShell, Td, Th, Tr } from '@/components/ui/table';
import {
  useDeleteInvoice,
  useDiscardInvoice,
  useInvoices,
  usePrefetchInvoice,
  useSetInvoiceStatus
} from '@/hooks/useInvoices';
import { useBillingNouns } from '@/hooks/useSession';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useDebounced } from '@/hooks/useDebounced';
import { formatDate, hoursShort, money } from '@/lib/format';
import type { InvoiceStatus, InvoiceSummary } from '@/types';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'sent', label: 'Sent' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'archived', label: 'Archived' }
] as const;

type TabValue = (typeof TABS)[number]['value'];

export function InvoicesPage() {
  const nouns = useBillingNouns();
  const navigate = useNavigate();
  const prefetch = usePrefetchInvoice();

  const [status, setStatus] = useLocalStorage<TabValue>('billable.invoiceTab', 'all');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 250);

  const invoices = useInvoices({ status, search, page });
  const setInvoiceStatus = useSetInvoiceStatus();
  const discard = useDiscardInvoice();
  const remove = useDeleteInvoice();

  const [confirm, setConfirm] = useState<
    { kind: 'discard' | 'delete'; invoice: InvoiceSummary } | null
  >(null);

  const rows = invoices.data?.items ?? [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Page
      wide
      title="Invoices"
      description="Everything you've billed, and everything still waiting to go out."
      actions={
        <Button variant="primary" asChild>
          <Link to="/invoices/new">
            <Plus />
            New invoice
          </Link>
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={status}
          onValueChange={(v) => {
            setStatus(v as TabValue);
            setPage(1);
          }}
        >
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--text-subtle)]" />
          <Input
            className="w-64 pl-9"
            placeholder={`Search number or ${nouns.clientLower}`}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <TableShell>
        <TableScroll minWidth="min-w-0">
          <thead>
            <tr>
              <Th width="9rem">Number</Th>
              <Th width="14rem">{nouns.client}</Th>
              <Th width="9rem">Issued</Th>
              <Th width="9rem">Due</Th>
              <Th align="right" width="6rem">
                Hours
              </Th>
              <Th align="right" width="8rem">
                Amount
              </Th>
              <Th width="7rem">Status</Th>
              <Th align="right" width="4rem">
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {invoices.isPending ? (
              Array.from({ length: 10 }).map((_, i) => (
                <SkeletonRow
                  key={i}
                  widths={['70%', '85%', '65%', '65%', '45%', '60%', '55%', '30%']}
                />
              ))
            ) : invoices.isError ? (
              <tr>
                <Td colSpan={8}>
                  <ErrorState
                    message={invoices.error instanceof Error ? invoices.error.message : undefined}
                    onRetry={() => void invoices.refetch()}
                  />
                </Td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <Td colSpan={8}>
                  <EmptyState
                    icon={FileText}
                    title={search ? 'No invoices match that search' : 'No invoices here yet'}
                    description={
                      search
                        ? 'Try a different invoice number or name.'
                        : 'Quick Invoice turns uninvoiced time into drafts in one pass.'
                    }
                    action={
                      search ? null : (
                        <Button variant="primary" asChild>
                          <Link to="/quick-invoice">Open Quick Invoice</Link>
                        </Button>
                      )
                    }
                  />
                </Td>
              </tr>
            ) : (
              rows.map((invoice) => {
                const processing = !!invoice.paymentSubmittedAt;
                const overdue =
                  invoice.status === 'sent' &&
                  !processing &&
                  !!invoice.dueDate &&
                  invoice.dueDate < today;
                return (
                  <Tr
                    key={invoice.id}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    pending={setInvoiceStatus.isPending && setInvoiceStatus.variables?.id === invoice.id}
                  >
                    <Td className="font-medium tnum" onMouseEnter={() => prefetch(invoice.id)}>
                      {invoice.invoiceNumber}
                    </Td>
                    <Td className="truncate">{invoice.clientName ?? '—'}</Td>
                    <Td className="whitespace-nowrap text-muted">{formatDate(invoice.issueDate)}</Td>
                    <Td className="whitespace-nowrap text-muted">{formatDate(invoice.dueDate)}</Td>
                    <Td align="right" className="tnum text-muted">
                      {hoursShort(invoice.totalHours)}
                    </Td>
                    <Td align="right" className="tnum font-medium">
                      {money(invoice.totalAmount)}
                    </Td>
                    <Td>
                      <StatusBadge
                        status={invoice.status}
                        overdue={overdue}
                        processing={processing}
                      />
                    </Td>
                    <Td align="right">
                      <div onClick={(e) => e.stopPropagation()}>
                        <RowMenu
                          invoice={invoice}
                          onStatus={(next) =>
                            setInvoiceStatus.mutate({ id: invoice.id, status: next })
                          }
                          onDiscard={() => setConfirm({ kind: 'discard', invoice })}
                          onDelete={() => setConfirm({ kind: 'delete', invoice })}
                        />
                      </div>
                    </Td>
                  </Tr>
                );
              })
            )}
          </tbody>
        </TableScroll>

        <Pagination
          label="invoices"
          page={invoices.data?.page ?? 1}
          totalPages={invoices.data?.totalPages ?? 1}
          totalCount={invoices.data?.totalCount ?? 0}
          pageSize={invoices.data?.pageSize ?? 50}
          onChange={setPage}
        />
      </TableShell>

      <ConfirmDialog
        open={Boolean(confirm)}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.kind === 'delete' ? 'Delete this invoice?' : 'Discard this draft?'}
        description={
          confirm?.kind === 'delete'
            ? `Invoice ${confirm.invoice.invoiceNumber} will be removed along with its line items. This cannot be undone.`
            : `Invoice ${confirm?.invoice.invoiceNumber} will be removed and its time entries become billable again.`
        }
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Discard'}
        destructive
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === 'delete') remove.mutate({ id: confirm.invoice.id });
          else discard.mutate({ id: confirm.invoice.id });
          setConfirm(null);
        }}
      />
    </Page>
  );
}

function RowMenu({
  invoice,
  onStatus,
  onDiscard,
  onDelete
}: {
  invoice: InvoiceSummary;
  onStatus: (status: InvoiceStatus) => void;
  onDiscard: () => void;
  onDelete: () => void;
}) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="ghost" size="iconSm" aria-label={`Actions for ${invoice.invoiceNumber}`}>
          <MoreHorizontal />
        </Button>
      </MenuTrigger>
      <MenuContent>
        {invoice.status !== 'paid' && invoice.status !== 'archived' ? (
          <MenuItem onSelect={() => onStatus('paid')}>
            <CheckCircle2 />
            Mark as paid
          </MenuItem>
        ) : null}
        {invoice.status === 'draft' ? (
          <MenuItem onSelect={() => onStatus('sent')}>
            <Send />
            Mark as sent
          </MenuItem>
        ) : null}
        {invoice.status === 'paid' ? (
          <MenuItem onSelect={() => onStatus('archived')}>
            <Archive />
            Archive
          </MenuItem>
        ) : null}
        {invoice.status === 'sent' || invoice.status === 'paid' ? (
          <MenuItem onSelect={() => onStatus('draft')}>
            <Undo2 />
            Back to draft
          </MenuItem>
        ) : null}
        {invoice.status === 'draft' || invoice.status === 'archived' ? (
          <>
            <MenuSeparator />
            <MenuItem
              destructive
              onSelect={() => (invoice.status === 'draft' ? onDiscard() : onDelete())}
            >
              <Trash2 />
              {invoice.status === 'draft' ? 'Discard draft' : 'Delete'}
            </MenuItem>
          </>
        ) : null}
      </MenuContent>
    </Menu>
  );
}
